import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ChevronRight } from "lucide-react";
import { SITE } from "@/constants/testIds";

const NAV_ITEMS = [
  { id: "home", label: "Home", testId: SITE.navHome, href: "#home" },
  { id: "services", label: "Services", testId: SITE.navServices, href: "#services" },
  { id: "projects", label: "Projects", testId: SITE.navProjects, href: "#projects" },
  { id: "about", label: "About", testId: SITE.navAbout, href: "#about" },
  { id: "contact", label: "Contact", testId: SITE.navContact, href: "#contact" },
];

function scrollToAnchor(hash) {
  const el = document.querySelector(hash);
  if (!el) return;
  if (window.__lenis) {
    window.__lenis.scrollTo(el, { offset: -80, duration: 1.2 });
  } else {
    el.scrollIntoView({ behavior: "smooth", block: "start" });
  }
}

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const onLinkClick = (e, href) => {
    e.preventDefault();
    setMobileOpen(false);
    setTimeout(() => scrollToAnchor(href), 50);
  };

  return (
    <motion.header
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
      className="fixed top-0 inset-x-0 z-50 px-4 md:px-8 pt-4"
    >
      <nav
        className={`mx-auto max-w-7xl flex items-center justify-between rounded-full px-4 md:px-6 h-14 md:h-16 transition-all duration-500 ${
          scrolled ? "tb-glass shadow-[0_10px_40px_-12px_rgba(0,0,0,0.15)]" : "tb-glass"
        }`}
      >
        {/* Logo */}
        <a
          href="#home"
          data-testid={SITE.navHome}
          onClick={(e) => onLinkClick(e, "#home")}
          className="flex items-center group"
        >
          <img
            src="/three-balls-logo.webp"
            alt="Three Balls"
            className="h-6 md:h-7 lg:h-8 w-auto select-none pointer-events-none"
            draggable={false}
          />
        </a>

        {/* Desktop nav */}
        <div className="hidden md:flex items-center gap-8">
          {NAV_ITEMS.map((item) => (
            <a
              key={item.id}
              href={item.href}
              data-testid={item.testId}
              onClick={(e) => onLinkClick(e, item.href)}
              className="tb-nav-link text-sm"
            >
              {item.label}
            </a>
          ))}
        </div>

        {/* CTA */}
        <div className="flex items-center gap-2">
          <a
            href="#contact"
            data-testid={SITE.navCtaContact}
            onClick={(e) => onLinkClick(e, "#contact")}
            className="hidden md:inline-flex items-center gap-2 pl-4 pr-1.5 py-1.5 rounded-full bg-[#1A1A1A] text-white text-sm font-semibold hover:bg-[#E9298A] transition-colors duration-300 group"
          >
            Let's talk
            <span className="w-7 h-7 rounded-full bg-[#FFE600] text-[#1A1A1A] flex items-center justify-center group-hover:rotate-45 transition-transform duration-300">
              <ChevronRight className="w-4 h-4" strokeWidth={2.5} />
            </span>
          </a>
          <button
            data-testid={SITE.navMobileToggle}
            onClick={() => setMobileOpen((v) => !v)}
            className="md:hidden w-10 h-10 rounded-full border border-[#1A1A1A] flex items-center justify-center bg-white/70 active:scale-95 transition-transform"
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </nav>

      {/* Mobile menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.25 }}
            className="md:hidden mx-auto max-w-7xl mt-3 tb-glass rounded-3xl px-6 py-6 shadow-xl"
          >
            <ul className="flex flex-col gap-2">
              {NAV_ITEMS.map((item) => (
                <li key={item.id}>
                  <a
                    href={item.href}
                    onClick={(e) => onLinkClick(e, item.href)}
                    className="block text-xl font-display font-bold py-2 hover:text-[#E9298A] transition-colors"
                  >
                    {item.label}
                  </a>
                </li>
              ))}
              <li className="pt-2">
                <a
                  href="#contact"
                  onClick={(e) => onLinkClick(e, "#contact")}
                  className="inline-flex items-center gap-2 pl-4 pr-1.5 py-1.5 rounded-full bg-[#1A1A1A] text-white text-sm font-semibold"
                >
                  Let's talk
                  <span className="w-7 h-7 rounded-full bg-[#FFE600] text-[#1A1A1A] flex items-center justify-center">
                    <ChevronRight className="w-4 h-4" strokeWidth={2.5} />
                  </span>
                </a>
              </li>
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
